import React, { useEffect, useRef } from 'react';
import { useThree, useFrame, invalidate } from '@react-three/fiber';
import * as THREE from 'three';
import { editorStore } from '../../store/editorStore';
import { observer } from 'mobx-react-lite';
import { projectToWorld } from '../FloorPlan/projectToWorld';
import { applyAlignmentAfterSnap } from '../FloorPlan/applyAlignmentAfterSnap';
import { floorplannerStore } from '../../store/floorplannerStore';
import { AlignmentLineType, RulerLineType, WallConnectionEnd } from '../../types/floorplannerTypes';
import { runInAction } from 'mobx';
import { updateAlignmentLines, updateAlignmentLinesForObject } from '../FloorPlan/updateAligmentLines';
import { renderStore } from '../../store/renderStore';
import { findSymbolFootprintSnapPoint } from '../../utils/symbolFootprintSnapping';

const MIN_RULER_LENGTH = 0.1;
const RULER_PREVIEW_ID = "__ruler-construction-preview__";

const RulerConstructionPlane = observer(() => {
  const {
    rulerConstructionMode,
    rulerConstructionModeAddNewLine,
    view3D,
  } = editorStore;
  const { gl, camera } = useThree();
  const addNewLine = useRef(false);
  const currentLine = useRef(undefined as string | undefined);
  const lastClickPosition = useRef(undefined as THREE.Vector2 | undefined);

  const updateFirstPointAlignmentPreview = (point: THREE.Vector2, shiftPressed: boolean) => {
    if (shiftPressed) {
      renderStore.clearAlignmentLines();
      return;
    }

    updateAlignmentLinesForObject({
      id: RULER_PREVIEW_ID,
      type: "singleLine",
      start: point.clone(),
      end: point.clone(),
    }, point.x, point.y, "end");
  };

  const snapToAlignmentLines = (point: THREE.Vector2, shiftPressed: boolean): THREE.Vector2 => {
    if (shiftPressed) {
      return point.clone();
    }

    let snappedX: number | undefined;
    let snappedY: number | undefined;
    let bestXDistance = Infinity;
    let bestYDistance = Infinity;
    const snapTolerance = editorStore.interactionSnappingTolerance();

    const checkLine = (line: AlignmentLineType) => {
      if (line.axis === "x") {
        const distance = Math.abs(point.x - line.hit.x);
        if (distance < snapTolerance && distance < bestXDistance) {
          snappedX = line.hit.x;
          bestXDistance = distance;
        }
      } else if (line.axis === "y") {
        const distance = Math.abs(point.y - line.hit.y);
        if (distance < snapTolerance && distance < bestYDistance) {
          snappedY = line.hit.y;
          bestYDistance = distance;
        }
      }
    };

    renderStore.alignmentLinesApproxMap.forEach(checkLine);
    renderStore.alignmentLinesMap.forEach(checkLine);

    const snappedPoint = point.clone();
    if (snappedX !== undefined) {
      snappedPoint.x = snappedX;
    }
    if (snappedY !== undefined) {
      snappedPoint.y = snappedY;
    }

    return snappedPoint;
  };

  /**
   * Snaps to symbol footprint corners/edge midpoints so a measurement chain
   * drawn across a piece of furniture reports exactly the dimension shown in
   * the property panel instead of wherever the pointer landed.
   */
  const snapToSymbolFootprints = (point: THREE.Vector2, shiftPressed: boolean): THREE.Vector2 | null => {
    if (shiftPressed || editorStore.isAltPressed) {
      return null;
    }

    const snap = findSymbolFootprintSnapPoint(
      point,
      floorplannerStore.symbolsMap.values(),
      editorStore.interactionSnappingTolerance(),
    );

    return snap ? snap.position.clone() : null;
  };

  const getSnappedFirstPoint = (point: THREE.Vector2, shiftPressed: boolean): THREE.Vector2 => {
    const symbolSnap = snapToSymbolFootprints(point, shiftPressed);
    if (symbolSnap) {
      renderStore.clearAlignmentLines();
      return symbolSnap;
    }

    updateFirstPointAlignmentPreview(point, shiftPressed);
    return snapToAlignmentLines(point, shiftPressed);
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (editorStore.rulerConstructionMode) {
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = getSnappedFirstPoint(new THREE.Vector2(pos[0], pos[1]), event.shiftKey);
      lastClickPosition.current = clickPosition;
      addNewLine.current = true;

      if (currentLine.current) {
        // Store the ruler ID before clearing anything
        const rulerId = currentLine.current;
        const ruler = floorplannerStore.rulerLinesMap.get(rulerId);

        if (ruler && ruler.start.distanceTo(ruler.end) <= MIN_RULER_LENGTH) {
          floorplannerStore.removeRulerLine(rulerId);
          editorStore.clearSelections();
          editorStore.setRulerLineEditingLength(undefined);
          editorStore.setRulerEditingLengthPanel(undefined);
          renderStore.clearAlignmentLines();
          currentLine.current = undefined;
          addNewLine.current = false;
          lastClickPosition.current = undefined;
          editorStore.setRulerConstructionMode(false);
          invalidate();
          return;
        }

        // Exit construction mode first
        editorStore.setRulerConstructionMode(false);

        // Use setTimeout to ensure selection happens AFTER all other click handlers (like SelectionPlane)
        // have finished executing and clearing selections.
        setTimeout(() => {
          runInAction(() => {
            const finishedRuler = floorplannerStore.rulerLinesMap.get(rulerId);
            if (finishedRuler) {
              editorStore.clearSelections();
              editorStore.addSelection(finishedRuler);
              floorplannerStore.selectRuler(rulerId);
            }
          });
          invalidate();
        }, 50);
      }
    }
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (editorStore.rulerConstructionMode && !editorStore.view3D) {
      document.body.classList.remove("cursor-vector")
      document.body.style.cursor = 'crosshair';
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const pointerPosition = new THREE.Vector2(pos[0], pos[1]);

      if (!currentLine.current && !addNewLine.current) {
        updateFirstPointAlignmentPreview(pointerPosition, event.shiftKey);
      }

      const distance = lastClickPosition.current?.distanceTo(pointerPosition) ?? 0;

      if (addNewLine.current && distance > MIN_RULER_LENGTH && lastClickPosition.current) {
        createNewLineSegment([pointerPosition.x, pointerPosition.y]);
      }

      // Update the line's position if it's already been created
      if (!addNewLine.current && currentLine.current) {
        const line = floorplannerStore.rulerLinesMap.get(currentLine.current);
        if (line) {
          const start = line.start;
          let end = pointerPosition.clone();

          // SNAPPING PRIORITY ORDER for ruler construction:
          // 1. Wall edge corners (for precise measurements from/to wall edges) - HIGHEST PRIORITY
          // 2. 45-degree angle snapping (when within 20 degrees of snap angle)
          // 3. Alignment lines - LOWEST PRIORITY

          let snappedWallAngle: number | undefined;
          let objectSnapApplied = false;
          const snapTolerance = editorStore.interactionSnappingTolerance();

          // PRIORITY 0: Symbol footprint corners, so measuring a piece of
          // furniture returns exactly its property panel dimensions.
          const symbolSnap = snapToSymbolFootprints(end, event.shiftKey);
          if (symbolSnap) {
            end = symbolSnap;
            objectSnapApplied = true;
          }

          // PRIORITY 1: Wall edge corner snapping (highest priority)
          // Allows rulers to snap to wall edges for precise measurements
          if (!objectSnapApplied && !event.shiftKey && !editorStore.isAltPressed) {
            const edgeSnapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(line.id, "end", end);
            if (edgeSnapCandidate?.snappingPosition &&
                end.distanceTo(edgeSnapCandidate.snappingPosition) < snapTolerance) {
              end = edgeSnapCandidate.snappingPosition.clone();
              objectSnapApplied = true;
            }
          } else if (editorStore.isAltPressed && !event.shiftKey) {
            // Center mode: check for center-based snapping
            const centerSnapPoint = floorplannerStore.getSnappingPositionForDrag(line.id, WallConnectionEnd, end);
            if (centerSnapPoint && end.distanceTo(centerSnapPoint) < snapTolerance) {
              end = centerSnapPoint.clone();
              objectSnapApplied = true;
            }
          }

          // PRIORITY 2: 45-degree angle snapping (only if no edge snap was applied)
          if (!objectSnapApplied && !event.shiftKey) {
            const deltaX = end.x - start.x;
            const deltaY = end.y - start.y;
            const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
            const currentDistance = start.distanceTo(end);
            const normalizedAngle = (angle + 360) % 360;
            const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
            let closestSnapAngle = snapAngles.reduce((prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev);
            const radianSnapAngle = closestSnapAngle * (Math.PI / 180);
            const snapEndX = start.x + currentDistance * Math.cos(radianSnapAngle);
            const snapEndY = start.y + currentDistance * Math.sin(radianSnapAngle);
            const snapDistance = end.distanceTo(new THREE.Vector2(snapEndX, snapEndY));

            if (snapDistance < editorStore.interactionSnappingTolerance(0.2)) {
              end.set(snapEndX, snapEndY);
              snappedWallAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
            }
          }

          // PRIORITY 3: Alignment snapping (lowest priority, skip if edge snap was applied)
          const currentPositions = { start: start.clone(), end: end.clone() };
          const skipAlignment = objectSnapApplied;
          const alignmentResult = skipAlignment ? { applied: false, positions: null } : applyAlignmentAfterSnap({
            objectId: line.id,
            intendedPoint: end,
            connectionEnd: WallConnectionEnd,
            alignmentKey: "end",
            suppressSnapping: false,
            existingPositions: currentPositions,
            snappedWallAngle: snappedWallAngle
          });

          if (alignmentResult.applied && alignmentResult.positions) {
            end.copy(alignmentResult.positions.end);
          }

          floorplannerStore.updateObjectPosition(currentLine.current, start, end);
          updateAlignmentLines(line.id, end.x, end.y, "end");
          // Update the wall's outer length in property panel
          const rulerLength = floorplannerStore.rulerLineLength(line.id);
          // Set the length edit box with id="rulerLength" in focus in the property panel
          editorStore.setRulerEditingLengthPanel(rulerLength);

        }
      }
    }
  };

  const createNewLineSegment = (pos: [number, number]) => {
    if (lastClickPosition.current && currentLine) {
      const line = floorplannerStore.rulerLinesMap.get(currentLine.current as string);
      const start = currentLine.current && line ?
        line.end.clone() :
        lastClickPosition.current.clone();
      const end = new THREE.Vector2(pos[0], pos[1]);
      const newLine: RulerLineType = {
        id: floorplannerStore.generateId(),
        type: "rulerLine",
        start: start,
        end: end,
      };
      floorplannerStore.addRulerLine(newLine);
      editorStore.setRulerLineEditingLength(newLine.id);
      currentLine.current = newLine.id;
      addNewLine.current = false;

      // Select the new ruler line
      editorStore.clearSelections();
      floorplannerStore.selectRuler(newLine.id);
      editorStore.addSelection(newLine);
      invalidate();

    }
  };

  useEffect(() => {
    if (currentLine.current && rulerConstructionModeAddNewLine) {
      editorStore.setRulerConstructionModeAddNewLine(false);
      const line = floorplannerStore.rulerLinesMap.get(currentLine.current);
      if (line) {
        addNewLine.current = true;
        editorStore.setRulerConstructionMode(false);
      }
    }
  }, [rulerConstructionModeAddNewLine]);

  useEffect(() => {
    if (rulerConstructionMode) {
      // Entering construction mode - reset state for new ruler creation
      renderStore.clearAlignmentLines();
      editorStore.setRulerLineEditingLength(undefined);
      editorStore.setRulerEditingLengthPanel(undefined);
      addNewLine.current = false;
      currentLine.current = undefined;
      lastClickPosition.current = undefined;
      document.body.classList.remove("cursor-vector")
      document.body.style.cursor = 'crosshair';
      floorplannerStore.setBlockDirty(true);
    } else {
      const completedLineId = currentLine.current;
      // Exiting construction mode - preserve selection/editing state for the newly created ruler
      // Only reset non-essential state
      if (currentLine.current) {
        const line = floorplannerStore.rulerLinesMap.get(currentLine.current);
        if (line && line.start.distanceTo(line.end) <= MIN_RULER_LENGTH) {
          floorplannerStore.removeRulerLine(line.id);
          editorStore.clearSelections();
        }
      }
      renderStore.clearAlignmentLines();
      addNewLine.current = false;
      currentLine.current = undefined;
      lastClickPosition.current = undefined;
      document.body.style.cursor = 'auto';
      floorplannerStore.setBlockDirty(false);
      if (completedLineId) {
        floorplannerStore.setDirty();
      }
    }
  }, [rulerConstructionMode]);

  return (
    <mesh
      position={[0, 0, -0.01]}
      onPointerDown={(e) => handlePointerDown(e.nativeEvent)}
      onPointerMove={(e) => handlePointerMove(e.nativeEvent)}
      userData={{ id: 'ruler-construction-plane' }}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
});

export default RulerConstructionPlane;
